import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Share2, Handshake, Package, Wrench, Search, MapPin, ChevronDown, Loader2, Check, Link2, Eye } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { toast } from 'sonner';
import api from '@/lib/api';
import WelcomeVaultModal from '@/components/WelcomeVaultModal';
import FounderBanner from '@/components/FounderBanner';
import ActivityFeed from '@/components/ActivityFeed';
import FeaturedStories from '@/components/FeaturedStories';
import OptimizedImage from '@/components/OptimizedImage';
import UserBadges from '@/components/UserBadges';

const conditionLabels = { nuevo:'Nuevo', como_nuevo:'Como nuevo', bueno:'Buen estado', usado:'Usado' };
const neighborhoods = [
  { id: 'todo', label: 'Todo Ocoyoacac', location: 'Ocoyoacac' },
  { id: 'santa_maria', label: 'Santa Maria', location: 'Ocoyoacac', neighborhood: 'Santa Maria' },
  { id: 'san_antonio', label: 'San Antonio', location: 'Ocoyoacac', neighborhood: 'San Antonio' },
  { id: 'san_miguel', label: 'San Miguel', location: 'Ocoyoacac', neighborhood: 'San Miguel' },
  { id: 'coapanoaya', label: 'Coapanoaya', location: 'Ocoyoacac', neighborhood: 'Coapanoaya' },
  { id: 'tepexoyuca', label: 'Tepexoyuca', location: 'Ocoyoacac', neighborhood: 'Tepexoyuca' },
  { id: 'acazulco', label: 'Acazulco', location: 'Ocoyoacac', neighborhood: 'Acazulco' },
  { id: 'cholula', label: 'Cholula', location: 'Ocoyoacac', neighborhood: 'Cholula' },
  { id: 'santiaguito', label: 'Santiaguito', location: 'Ocoyoacac', neighborhood: 'Santiaguito' },
  { id: 'el_pedregal', label: 'El Pedregal', location: 'Ocoyoacac', neighborhood: 'El Pedregal' },
  { id: 'el_pirame', label: 'El Pirame', location: 'Ocoyoacac', neighborhood: 'El Pirame' },
  { id: 'benevento', label: 'Benevento', location: 'Ocoyoacac', neighborhood: 'Benevento' },
];

const getMediaUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${url}`);
const sora = { fontFamily: 'Sora, sans-serif' };

export default function FeedPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [likes, setLikes] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [activeTab, setActiveTab] = useState('oferta');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();

  const fetchItems = useCallback((pageNum = 1, append = false) => {
    if (pageNum > 1) setLoadingMore(true); else setLoading(true);
    const params = new URLSearchParams();
    params.append('page', String(pageNum));
    params.append('limit', '20');
    if (locationFilter) {
      const hood = neighborhoods.find(n => n.id === locationFilter);
      if (hood) {
        params.append('location', hood.location);
        if (hood.neighborhood) params.append('neighborhood', hood.neighborhood);
      }
    }
    if (activeTab === 'se_busca') params.append('item_type', 'wanted');
    else if (typeFilter) params.append('item_type', typeFilter);
    api.get(`/items?${params.toString()}`).then(r => {
      const res = r.data;
      const newItems = res.data || [];
      const filtered = activeTab === 'se_busca'
        ? newItems.filter(i => i.item_type === 'wanted')
        : newItems.filter(i => i.item_type !== 'wanted');
      if (append) setItems(prev => [...prev, ...filtered]);
      else setItems(filtered);
      setHasMore(res.pagination?.hasMore || false);
      setPage(pageNum);
    }).catch(() => {}).finally(() => { setLoading(false); setLoadingMore(false); });
  }, [activeTab, locationFilter, typeFilter]);

  useEffect(() => {
    setPage(1);
    fetchItems(1, false);
    api.get('/auth/me').then(r => {
      setIsLoggedIn(true);
      if (!r.data.has_opened_vault) setShowVault(true);
      api.get('/users/me/likes').then(r => {
        const map = {};
        (r.data || []).forEach(id => { map[id] = true; });
        setLikes(map);
      }).catch(() => {});
    }).catch(() => setIsLoggedIn(false));
  }, [fetchItems]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) fetchItems(page + 1, true);
    }, { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, page, fetchItems]);

  const handleLike = async (item) => {
    if (!isLoggedIn) {
      sessionStorage.setItem('bisne_next', `/item/${item.item_id}`);
      navigate(`/login?next=${encodeURIComponent(`/item/${item.item_id}`)}`);
      return;
    }
    const wasLiked = likes[item.item_id];
    setLikes(p => ({ ...p, [item.item_id]: !wasLiked }));
    try { await api.post(`/items/${item.item_id}/like`); }
    catch { setLikes(p => ({ ...p, [item.item_id]: wasLiked })); }
  };

  const handleAction = (item) => {
    if (isLoggedIn === false) {
      sessionStorage.setItem('bisne_next', `/item/${item.item_id}`);
      navigate(`/login?next=${encodeURIComponent(`/item/${item.item_id}`)}`);
    } else if (item.item_type === 'wanted') {
      navigate(`/deal/${item.item_id}?preMsg=${encodeURIComponent('Hola, vi que buscas esto. Yo tengo algo que te puede interesar.')}`);
    } else {
      navigate(`/deal/${item.item_id}`);
    }
  };

  const handleShare = async (item) => {
    const url = `${window.location.origin}/item/${item.item_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${item.title} - $${item.price?.toLocaleString()} MXN`, text: `Mira esto en Bisne: ${item.title}`, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success('Link copiado!'); } catch { toast.error('Error al copiar'); }
    }
  };

  const handleCopyLink = async (item) => {
    const url = `${window.location.origin}/item/${item.item_id}`;
    try { await navigator.clipboard.writeText(url); toast.success('Link copiado!'); } catch { toast.error('Error al copiar'); }
  };

  if (loading) return (
    <div className="h-[100dvh] flex items-center justify-center" style={{ background: '#080808' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#f5c518', borderTopColor: 'transparent' }} />
    </div>
  );

  const isSold = (item) => item.status === 'sold';
  const activeHood = neighborhoods.find(n => n.id === locationFilter);
  const catLabel = typeFilter === 'product' ? 'Producto' : typeFilter === 'service' ? 'Servicio' : 'Categoria';

  /* ── Neighborhood Chips (shared between empty + main) ── */
  const NeighborhoodChips = () => (
    <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto scrollbar-hide">
      {neighborhoods.map(hood => {
        const isActive = locationFilter === hood.id;
        return (
          <button key={hood.id} data-testid={`feed-hood-${hood.id}`}
            onClick={() => setLocationFilter(isActive ? '' : hood.id)}
            className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all"
            style={{
              fontFamily: 'Sora, sans-serif',
              background: isActive ? '#f5c51815' : '#0f0f0f',
              color: isActive ? '#f5c518' : '#555',
              border: isActive ? '1px solid #f5c51840' : '1px solid #1a1a1a',
            }}>
            {hood.label}
          </button>
        );
      })}
    </div>
  );

  /* ── EMPTY STATE ── */
  if (items.length === 0 && !loadingMore) return (
    <div data-testid="feed-empty" className="h-[100dvh] flex flex-col" style={{ background: '#080808', ...sora }}>
      <div className="px-4 py-4 text-center flex items-center justify-center gap-3 lg:hidden">
        <img src="/logo.png" alt="Bisne" className="h-9 w-9 object-contain shrink-0" />
        <div>
          <h1 className="text-lg font-bold leading-tight" style={{ color: '#f5c518' }}>El Marketplace de la Raza</h1>
          <p className="text-[10px] mt-0.5" style={{ color: '#555' }}>Compra · Vende · Cambia · Sin comisiones</p>
        </div>
      </div>
      <div className="sticky top-0 z-30" style={{ background: '#080808' }}>
        <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
        <NeighborhoodChips />
      </div>
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
        {locationFilter ? (
          <>
            <MapPin className="w-12 h-12" style={{ color: '#333' }} />
            <h2 data-testid="feed-empty-location" className="text-lg font-bold text-center" style={{ color: '#d5d5d5' }}>No hay articulos en {activeHood?.label || 'esta zona'}</h2>
            <p className="text-sm text-center max-w-xs" style={{ color: '#555' }}>Amplia tu busqueda o quita el filtro.</p>
            <button data-testid="feed-clear-location" onClick={() => setLocationFilter('')}
              className="mt-2 rounded-full px-6 py-2.5 font-semibold text-sm" style={{ background: '#f5c51815', color: '#f5c518', border: '1px solid #f5c51830' }}>
              Ver todos los articulos
            </button>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold" style={{ color: '#f5c518' }}>Sin articulos</h2>
            <p className="text-sm text-center" style={{ color: '#555' }}>Se el primero en publicar</p>
          </>
        )}
      </div>
    </div>
  );

  /* ── MAIN FEED ── */
  return (
    <div className="min-h-[100dvh] w-full flex flex-col" style={{ background: '#080808', ...sora }}>
      {/* Hero Banner - hidden on desktop */}
      <div data-testid="feed-hero" className="px-4 py-3 shrink-0 flex items-center justify-center gap-3 lg:hidden">
        <img src="/logo.png" alt="Bisne" className="h-9 w-9 object-contain shrink-0" />
        <div className="text-center">
          <h1 className="text-lg sm:text-xl font-bold leading-tight" style={{ color: '#f5c518' }}>El Marketplace de la Raza</h1>
          <p className="text-[10px] sm:text-xs" style={{ color: '#555' }}>Compra · Vende · Cambia · Sin comisiones</p>
        </div>
      </div>

      {/* Growth: Banner + Stories + Ticker (guests) */}
      {isLoggedIn === false && (
        <div className="shrink-0">
          <FounderBanner isLoggedIn={isLoggedIn} />
          <FeaturedStories />
          <ActivityFeed />
        </div>
      )}
      {isLoggedIn && <FeaturedStories items={items} />}

      {/* Sticky Tabs + Filters */}
      <div className="sticky top-0 z-30 backdrop-blur-xl shrink-0" style={{ background: '#080808ee' }}>
        <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
        <NeighborhoodChips />
        {activeTab === 'oferta' && (
          <div className="flex items-center gap-2 px-3 pb-2">
            <button data-testid="feed-filter-category" onClick={() => setCatDrawerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium"
              style={{
                fontFamily: 'Sora, sans-serif',
                background: typeFilter ? '#f5c51815' : '#0f0f0f',
                color: typeFilter ? '#f5c518' : '#555',
                border: typeFilter ? '1px solid #f5c51840' : '1px solid #1a1a1a',
              }}>
              {typeFilter === 'service' ? <Wrench className="w-3 h-3" /> : <Package className="w-3 h-3" />}
              {catLabel}
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Category Drawer */}
      <Drawer open={catDrawerOpen} onOpenChange={setCatDrawerOpen}>
        <DrawerContent style={{ background: '#111', borderColor: '#1a1a1a' }}>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-lg font-bold" style={{ color: '#f5c518', fontFamily: 'Sora, sans-serif' }}>Categoria</DrawerTitle>
            <DrawerDescription className="text-xs" style={{ color: '#555', fontFamily: 'Sora, sans-serif' }}>Selecciona una opcion</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 pt-2 grid grid-cols-1 gap-2">
            {[
              { val: '', label: 'Todos', icon: null },
              { val: 'product', label: 'Producto', icon: Package },
              { val: 'service', label: 'Servicio', icon: Wrench },
            ].map(({ val, label, icon: Icon }) => (
              <button key={val || 'all'} data-testid={`feed-type-${val || 'all'}`}
                onClick={() => { setTypeFilter(val); setCatDrawerOpen(false); }}
                className="flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  background: typeFilter === val ? '#f5c518' : '#1a1a1a',
                  color: typeFilter === val ? '#000' : '#d5d5d5',
                }}>
                <span className="flex items-center gap-2">{Icon && <Icon className="w-4 h-4" />}{label}</span>
                {typeFilter === val && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Feed Content */}
      <div className="flex-1 overflow-y-auto page-with-nav">
        {activeTab === 'se_busca' ? (
          /* WANTED grid */
          <div data-testid="feed-wanted-grid" className="p-3 space-y-3">
            {items.length === 0 && (
              <div className="text-center py-16">
                <Search className="w-10 h-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: '#555', ...sora }} className="text-sm">Nadie busca nada aun</p>
              </div>
            )}
            {items.map(item => (
              <div key={item.item_id} data-testid={`feed-wanted-${item.item_id}`}
                onClick={() => navigate(`/item/${item.item_id}`)}
                className="rounded-2xl cursor-pointer overflow-hidden active:scale-[0.99] transition-all"
                style={{ background: '#0f0f0f', border: '1px solid #f5c51820' }}>
                <div className="flex items-start gap-3 p-4">
                  <div className="w-1 self-stretch rounded-full shrink-0" style={{ background: '#f5c518' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: '#f5c51815', color: '#f5c518', border: '1px solid #f5c51830' }}>
                        <Search className="w-3 h-3 inline mr-0.5 -mt-0.5" />Se busca
                      </span>
                      {item.location && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: '#1a1a1a', color: '#555' }}><MapPin className="w-3 h-3 inline mr-0.5 -mt-0.5" />{item.location}</span>}
                    </div>
                    <h3 className="text-base font-bold leading-snug" style={{ color: '#d5d5d5' }}>{item.title}</h3>
                    {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: '#555' }}>{item.description}</p>}
                    <p className="text-lg font-bold mt-1.5" style={{ color: '#f5c518' }}>Hasta ${item.price?.toLocaleString()} MXN</p>
                  </div>
                  {item.images?.[0] && (
                    <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0" style={{ border: '1px solid #1a1a1a' }}>
                      <img src={getMediaUrl(item.images[0])} className="object-cover w-full h-full" alt="" />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {hasMore && <div ref={sentinelRef} className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#f5c518' }} /></div>}
          </div>
        ) : (
          /* ── CARD FEED ── */
          <div data-testid="feed-container">
            {items.map((item) => {
              const firstImage = item.images?.[0] || item.image_url;
              const imageCount = (item.images?.length || 0) + (item.video_url ? 1 : 0);
              const isService = item.item_type === 'service';
              const sold = item.is_sold || (isSold(item) && !isService);
              const closed = isService && item.is_closed;
              const unavailable = sold || closed;
              const liked = likes[item.item_id];
              const colony = item.neighborhood || item.location || '';
              const views = 15 + ((item.item_id || '').charCodeAt(5) || 0) % 70;
              const hasDiscount = item.discount_price && item.discount_price > 0 && item.discount_price < item.price;
              const isDealWeek = item.is_deal_of_the_week;
              const isDealFlash = item.is_deal_flash;
              const hasDeal = isDealWeek || isDealFlash;

              return (
                <div key={item.item_id} data-testid={`feed-item-${item.item_id}`}
                  className="lg:flex lg:gap-4"
                  style={{
                    padding: '10px 12px 0',
                    borderTop: hasDeal ? '2px solid #ef444460' : '1px solid #0d0d0d',
                    background: hasDeal ? '#0f0a0a' : 'transparent',
                  }}>

                  {/* Image container — square on desktop, wide on mobile */}
                  <div
                    className="relative overflow-hidden cursor-pointer lg:w-[220px] lg:h-[220px] lg:shrink-0"
                    onClick={() => navigate(`/item/${item.item_id}`)}
                    style={{ borderRadius: 18, border: '1px solid #1a1a1a', height: 195 }}
                  >
                    {firstImage ? (
                      <OptimizedImage
                        src={getMediaUrl(firstImage)}
                        alt={item.title}
                        thumbWidth={400}
                        className={`w-full h-full ${sold ? 'grayscale' : closed ? 'brightness-50' : ''}`}
                      />
                    ) : item.video_url ? (
                      <video src={getMediaUrl(item.video_url)} className={`object-cover w-full h-full ${sold ? 'grayscale' : closed ? 'brightness-50' : ''}`} muted playsInline preload="metadata" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: '#111' }}>
                        <Package className="w-10 h-10" style={{ color: '#222' }} />
                      </div>
                    )}

                    {/* Sold stamp */}
                    {sold && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                        <div className="px-6 py-2 rounded-xl" style={{ background: '#ef444490', backdropFilter: 'blur(8px)', transform: 'rotate(-12deg)' }}>
                          <span className="text-xl font-bold text-white tracking-wider">VENDIDO</span>
                        </div>
                      </div>
                    )}

                    {/* Closed stamp */}
                    {closed && !sold && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                        <div className="px-5 py-2 rounded-xl" style={{ background: '#f9731690', backdropFilter: 'blur(8px)', transform: 'rotate(-6deg)' }}>
                          <span className="text-lg font-bold text-white tracking-wider">CERRADO</span>
                        </div>
                      </div>
                    )}

                    {/* Tags top-left */}
                    <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-10 flex-wrap">
                      {isDealWeek && (
                        <span data-testid={`feed-deal-badge-${item.item_id}`}
                          className="text-[10px] font-bold px-2 py-[3px] rounded-full flex items-center gap-1"
                          style={{ background: '#ef4444', color: '#fff' }}>
                          <span>🔥</span>Deal de la Semana
                        </span>
                      )}
                      {isDealFlash && !isDealWeek && (
                        <span data-testid={`feed-flash-badge-${item.item_id}`}
                          className="text-[10px] font-bold px-2 py-[3px] rounded-full flex items-center gap-1"
                          style={{ background: '#f97316', color: '#fff' }}>
                          <span>⚡</span>Deal Flash
                        </span>
                      )}
                      <span className="text-[10px] font-medium px-2 py-[3px] rounded-full flex items-center gap-1"
                        style={{ background: '#00000099', backdropFilter: 'blur(8px)', color: '#d5d5d5' }}>
                        {isService ? <><Wrench className="w-3 h-3" />Servicio</> : <><Package className="w-3 h-3" />Producto</>}
                      </span>
                      {!isService && item.condition && (
                        <span className="text-[10px] px-2 py-[3px] rounded-full"
                          style={{ background: '#00000099', backdropFilter: 'blur(8px)', color: '#aaa' }}>
                          {conditionLabels[item.condition] || item.condition}
                        </span>
                      )}
                    </div>

                    {/* Heart top-right */}
                    <button
                      data-testid={`feed-like-${item.item_id}`}
                      onClick={(e) => { e.stopPropagation(); handleLike(item); }}
                      className="absolute top-2.5 right-2.5 z-10 flex items-center justify-center"
                      style={{ width: 32, height: 32, borderRadius: '50%', background: '#00000099', backdropFilter: 'blur(8px)' }}>
                      <Heart className={`w-4 h-4 transition-all ${liked ? 'fill-red-500 text-red-500' : ''}`} style={{ color: liked ? '#ef4444' : '#d5d5d5' }} />
                    </button>

                    {/* Colony bottom-left */}
                    {colony && (
                      <div className="absolute bottom-2.5 left-2.5 z-10 flex items-center gap-1 px-2 py-1 rounded-full"
                        style={{ background: '#00000099', backdropFilter: 'blur(8px)' }}>
                        <MapPin className="w-3 h-3" style={{ color: '#888' }} />
                        <span className="text-[9px]" style={{ color: '#888' }}>{colony}</span>
                      </div>
                    )}

                    {/* Stats bottom-right */}
                    <div className="absolute bottom-2.5 right-2.5 z-10 flex items-center gap-1.5 px-2 py-1 rounded-full"
                      style={{ background: '#00000099', backdropFilter: 'blur(8px)' }}>
                      <span className="text-[9px] flex items-center gap-0.5" style={{ color: '#888' }}>
                        <Eye className="w-3 h-3" />{views}
                      </span>
                      <span className="text-[9px] flex items-center gap-0.5" style={{ color: '#888' }}>
                        <Heart className="w-2.5 h-2.5" />{item.offer_count || 0}
                      </span>
                    </div>
                  </div>

                  {/* Info — beside image on desktop, below on mobile */}
                  <div className="pt-2.5 pb-3 px-0.5 lg:pt-1 lg:flex-1 lg:min-w-0 lg:flex lg:flex-col lg:justify-between">
                    <div>
                      <h3 className="font-bold leading-snug cursor-pointer" onClick={() => navigate(`/item/${item.item_id}`)}
                        style={{ color: '#d5d5d5', fontSize: 15 }}>{item.title}</h3>
                      <p className="font-black mt-0.5" style={{ fontSize: 26 }}>
                        {hasDiscount ? (
                          <>
                            <span style={{ color: '#555', fontSize: 16, textDecoration: 'line-through', fontWeight: 600 }}>${item.price?.toLocaleString()}</span>
                            {' '}
                            <span style={{ color: '#ef4444' }}>${item.discount_price?.toLocaleString()}</span>
                            <span className="text-sm font-semibold ml-1.5 px-1.5 py-0.5 rounded-full" style={{ background: '#ef444420', color: '#ef4444', fontSize: 11 }}>
                              -{Math.round((1 - item.discount_price / item.price) * 100)}%
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#f5c518' }}>${item.price?.toLocaleString()}</span>
                        )}
                        {' '}<span className="text-sm font-semibold" style={{ color: '#555' }}>MXN</span>
                      </p>
                      <p className="mt-0.5 flex items-center gap-1" style={{ color: '#666', fontSize: 10 }}>
                        @{item.seller_name?.toLowerCase().replace(/\s/g, '_')}
                        <UserBadges user={item} prefix="seller_" size="sm" />
                        {colony ? ` · ${colony}` : ''}
                      </p>
                      {item.description && (
                        <p className="mt-1.5 line-clamp-2" style={{ color: '#555', fontSize: 11, lineHeight: 1.7 }}>
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* Action buttons */}
                    {!unavailable && (
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          data-testid={`feed-negociar-${item.item_id}`}
                          onClick={() => handleAction(item)}
                          className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 rounded-full py-3 lg:py-2 lg:px-6 font-bold text-sm active:scale-[0.97] transition-transform"
                          style={{
                            background: 'linear-gradient(135deg, #f5c518, #d4a017)',
                            color: '#000',
                            boxShadow: '0 4px 20px #f5c51840',
                            fontFamily: 'Sora, sans-serif',
                          }}>
                          <Handshake className="w-4 h-4" />
                          {isService ? 'Contactar' : 'Negociar'}
                        </button>
                        <button
                          data-testid={`feed-share-${item.item_id}`}
                          onClick={() => handleShare(item)}
                          className="flex items-center justify-center shrink-0 active:scale-95 transition-transform lg:hidden"
                          style={{ width: 44, height: 44, borderRadius: '50%', background: '#111', border: '1px solid #1a1a1a' }}>
                          <Share2 className="w-4 h-4" style={{ color: '#555' }} />
                        </button>
                        <button
                          data-testid={`feed-copy-${item.item_id}`}
                          onClick={() => handleCopyLink(item)}
                          className="flex items-center justify-center shrink-0 active:scale-95 transition-transform lg:hidden"
                          style={{ width: 44, height: 44, borderRadius: '50%', background: '#111', border: '1px solid #1a1a1a' }}>
                          <Link2 className="w-4 h-4" style={{ color: '#555' }} />
                        </button>
                      </div>
                    )}
                    {sold && (
                      <div className="mt-3 py-3 rounded-full text-center text-sm font-bold" style={{ background: '#111', color: '#555', border: '1px solid #1a1a1a' }}>
                        Ya no disponible
                      </div>
                    )}
                    {closed && !sold && (
                      <div className="mt-3 py-3 rounded-full text-center text-sm font-bold" style={{ background: '#111', color: '#f97316', border: '1px solid #f9731630' }}>
                        Cerrado por ahora
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {hasMore && <div ref={sentinelRef} className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#f5c518' }} /></div>}
          </div>
        )}
      </div>

      {showVault && <WelcomeVaultModal onClose={() => setShowVault(false)} />}
    </div>
  );
}

/* ── Tab Bar Component ── */
function TabBar({ activeTab, setActiveTab }) {
  return (
    <div data-testid="feed-tabs" className="flex" style={{ borderBottom: '1px solid #0d0d0d' }}>
      <button data-testid="feed-tab-oferta" onClick={() => setActiveTab('oferta')}
        className="flex-1 py-2.5 text-sm font-semibold text-center transition-colors"
        style={{
          fontFamily: 'Sora, sans-serif',
          color: activeTab === 'oferta' ? '#f5c518' : '#555',
          borderBottom: activeTab === 'oferta' ? '2px solid #f5c518' : '2px solid transparent',
        }}>
        Oferta
      </button>
      <button data-testid="feed-tab-busca" onClick={() => setActiveTab('se_busca')}
        className="flex-1 py-2.5 text-sm font-semibold text-center flex items-center justify-center gap-1.5 transition-colors"
        style={{
          fontFamily: 'Sora, sans-serif',
          color: activeTab === 'se_busca' ? '#f5c518' : '#555',
          borderBottom: activeTab === 'se_busca' ? '2px solid #f5c518' : '2px solid transparent',
        }}>
        <Search className="w-3.5 h-3.5" /> Se busca
      </button>
    </div>
  );
}
